<template>
    <div>
        <Alert
            class="mb-4 mt-0"
            v-if="isCardAboutToExpire && isDefault"
            variant="error"
        >
            <span class="font-medium tracking-[-0.02em]">
                {{ $t("billing.card.alert_card_expire.title") }}
            </span>

            <span class="mt-2 text-sm block tracking-[-0.01em]">
                {{ $t("billing.card.alert_card_expire.description") }}
            </span>
        </Alert>

        <div class="gap-4 flex">
            <div>
                <div
                    class="h-10 rounded-md border-iron-200 px-2 flex items-center justify-center border"
                >
                    <IconVisa v-if="method.brand === 'visa'" />
                    <IconMastercard v-if="method.brand === 'mastercard'" />
                </div>
            </div>
            <div class="gap-3">
                <div class="gap-4 flex items-start">
                    <div>
                        <p class="text-sm font-medium text-iron-700">
                            <span class="capitalize">{{ method.brand }}</span>
                            {{ $t("billing.card.ending_in") }}
                            {{ method.last4 }}
                        </p>
                        <p class="text-sm text-iron-500 tracking-[-0.01em]">
                            {{ $t("billing.card.expiry_short") }}
                            {{ method.exp_month }}/{{ method.exp_year }}
                        </p>
                    </div>
                    <div
                        v-if="isDefault"
                        class="rounded-md bg-z-primary-600 text-xs font-medium text-white flex px-[9px] py-[3px] tracking-[-0.01em] uppercase"
                    >
                        {{ $t("default") }}
                    </div>
                </div>

                <div class="mt-2 gap-4 flex items-center">
                    <Button
                        v-if="!isCardAboutToExpire"
                        @click="updateDefaultPaymentMethod(method.id)"
                        :loading="defaultPaymentMethodForm.processing"
                        :disabled="isDefault || isCardAboutToExpire"
                        variant="ghost"
                    >
                        {{ $t("billing.card.set_as_default") }}
                    </Button>

                    <Button
                        v-if="canDeletePaymentMethod"
                        @click="deletePaymentMethod(method.id)"
                        :loading="deletePaymentMethodForm.processing"
                        variant="ghost"
                        >{{ $t("remove") }}</Button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useForm } from "@inertiajs/vue3";
import IconVisa from "./IconVisa.svg";
import IconMastercard from "./IconMastercard.svg";
import route from "ziggy-js";
import { PaymentMethod, Plan } from "../types";
import { Button } from "@/components/ui/button";
import { computed } from "vue";
import { checkCardIsAboutToExpire } from "../utils/cardIsAboutToExpire";
import { Alert } from "@/components/ui/alert";

interface Props {
    method: PaymentMethod;
    defaultPaymentMethod?: PaymentMethod;
    activePlan?: Plan;
}

const props = defineProps<Props>();

const defaultPaymentMethodForm = useForm<{ paymentMethodId: string | null }>({
    paymentMethodId: null,
});

const deletePaymentMethodForm = useForm<{ paymentMethod: string | null }>({
    paymentMethod: null,
});

function updateDefaultPaymentMethod(paymentMethodId: string) {
    defaultPaymentMethodForm.paymentMethodId = paymentMethodId;
    defaultPaymentMethodForm.put(route("app.default-payment-method.update"));
}

function deletePaymentMethod(paymentMethodId: string) {
    deletePaymentMethodForm.paymentMethod = paymentMethodId;
    deletePaymentMethodForm.delete(route("app.payment-methods.delete"));
}

const isDefault = computed(() => {
    if (!props.defaultPaymentMethod) {
        return false;
    }

    return props.method.id === props.defaultPaymentMethod.id;
});

const canDeletePaymentMethod = computed(() => {
    if (props.activePlan === undefined) {
        return true;
    }

    return !isDefault.value;
});

const isCardAboutToExpire = computed(() => {
    return checkCardIsAboutToExpire(
        props.method.exp_year,
        props.method.exp_month,
    );
});
</script>
